import React, { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Spinner, SpinnerSize } from '@fluentui/react'
import {
  DailyProductionReading,
  errorToString,
  getOwnerMonitoringEvidenceLabel,
  OwnerMonitoringEquipmentItem,
  OwnerMonitoringSystemSummary,
  SolarMonitoringConnectionStatus,
  SolarMonitoringEvidence,
  SolarMonitoringProvider,
} from '@oneethos/shared'
import { toast } from 'react-toastify'
import api from '../api-client'
import { Section } from '../components'
import { useApi } from '../hooks'
import './ownerMonitoringSiteDetailPage.scss'

type MonitoringSiteDetail = {
  id: string
  provider: SolarMonitoringProvider
  externalSystemId: string
  name?: string
  address?: string
  providerStatus?: string
  systemSizeWatts?: number
  timezone?: string
  installationDate?: string
  equipment: OwnerMonitoringEquipmentItem[]
  summary?: OwnerMonitoringSystemSummary
  status: SolarMonitoringConnectionStatus
  productionReadings: DailyProductionReading[]
  evidence?: SolarMonitoringEvidence
  lastSyncedAt?: string
  lastEnergySyncedAt?: string
  lastMetadataSyncedAt?: string
  lastSyncErrorCategory?: string
  lastProviderRequestCount?: number
}

type MetadataItemProps = {
  label: string
  value?: string | number
}

const MetadataItem = ({ label, value }: MetadataItemProps) => {
  if (value === undefined || value === '') {
    return null
  }
  return (
    <div className="site-metadata-item">
      <span className="site-metadata-label">{label}</span>
      <span className="site-metadata-value">{value}</span>
    </div>
  )
}

const formatTimestamp = (value?: string | Date): string | undefined => {
  if (!value) {
    return undefined
  }
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toLocaleString()
}

const formatKwh = (productionWh: number): string => `${(productionWh / 1000).toFixed(2)} kWh`

const getChartScaleWh = (readings: DailyProductionReading[]): number => {
  const sorted = readings
    .map(reading => reading.productionWh)
    .filter(productionWh => productionWh > 0)
    .sort((left, right) => left - right)
  if (sorted.length === 0) {
    return 1
  }
  const percentileIndex = Math.floor(sorted.length * 0.95)
  return Math.max(sorted[Math.min(percentileIndex, sorted.length - 1)], 1)
}

type ProductionBarColumnProps = {
  reading: DailyProductionReading
  scaleWh: number
}

const ProductionBarColumn = ({ reading, scaleWh }: ProductionBarColumnProps) => {
  const isClipped = reading.productionWh > scaleWh
  const heightPercent = Math.min(100, (reading.productionWh / scaleWh) * 100)
  const label = `${reading.date}: ${formatKwh(reading.productionWh)}`

  return (
    <div
      aria-label={label}
      className="production-bar-column"
      role="img"
      tabIndex={0}
      title={label}
    >
      <div
        className={isClipped ? 'production-bar production-bar-clipped' : 'production-bar'}
        style={{ height: `${Math.max(2, heightPercent)}%` }}
      />
      <span className="production-tooltip">
        <span className="production-tooltip-date">{reading.date}</span>
        <strong>{formatKwh(reading.productionWh)}</strong>
        {isClipped ? <span className="production-tooltip-note">above chart scale</span> : null}
      </span>
    </div>
  )
}

type ProductionChartProps = {
  readings: DailyProductionReading[]
  currentPowerW?: number
}

const ProductionChart = ({ readings, currentPowerW }: ProductionChartProps) => {
  if (readings.length === 0) {
    return <div className="alert alert-secondary">No production readings stored yet.</div>
  }
  const sortedReadings = [...readings].sort((left, right) => left.date.localeCompare(right.date))
  const scaleWh = getChartScaleWh(sortedReadings)
  const clippedCount = sortedReadings.filter(reading => reading.productionWh > scaleWh).length
  const totalKwh = sortedReadings.reduce((total, reading) => total + reading.productionWh, 0) / 1000
  const averageKwh = totalKwh / sortedReadings.length
  const peakReading = sortedReadings.reduce((peak, reading) => (
    reading.productionWh > peak.productionWh ? reading : peak
  ), sortedReadings[0])
  const latestReading = sortedReadings[sortedReadings.length - 1]
  const middleReading = sortedReadings[Math.floor(sortedReadings.length / 2)]

  return (
    <div className="production-chart">
      <div className="row g-2 mb-3">
        <div className="col-6 col-md-3">
          <div className="production-stat">
            <span>Daily average</span>
            <strong>{averageKwh.toFixed(1)} kWh</strong>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="production-stat">
            <span>Peak day</span>
            <strong>{formatKwh(peakReading.productionWh)}</strong>
            <small>{peakReading.date}</small>
          </div>
        </div>
        <div className="col-6 col-md-3">
          <div className="production-stat">
            <span>Latest day</span>
            <strong>{formatKwh(latestReading.productionWh)}</strong>
            <small>{latestReading.date}</small>
          </div>
        </div>
        {currentPowerW !== undefined ? (
          <div className="col-6 col-md-3">
            <div className="production-stat">
              <span>Current power</span>
              <strong>{currentPowerW.toLocaleString()} W</strong>
            </div>
          </div>
        ) : null}
      </div>
      <div className="production-plot">
        <div className="production-scale">
          <span>{formatKwh(scaleWh)}</span>
          <span>0 kWh</span>
        </div>
        <div className="production-bars">
          {sortedReadings.map(reading => (
            <ProductionBarColumn key={reading.date} reading={reading} scaleWh={scaleWh} />
          ))}
        </div>
      </div>
      <div className="production-axis">
        <span>{sortedReadings[0].date}</span>
        <span>{middleReading.date}</span>
        <span>{latestReading.date}</span>
      </div>
      {clippedCount > 0 ? (
        <p className="text-muted small mt-2">
          Chart scaled to {formatKwh(scaleWh)} so typical days stay readable.
          {' '}{clippedCount === 1 ? '1 day is' : `${clippedCount} days are`} clipped at
          the top of the scale; hover for the reported value.
        </p>
      ) : null}
    </div>
  )
}

const EquipmentTable = ({ equipment }: { equipment: OwnerMonitoringEquipmentItem[] }) => {
  if (equipment.length === 0) {
    return <p className="text-muted">No equipment details were returned by the provider.</p>
  }
  const grouped = new Map<string, OwnerMonitoringEquipmentItem>()
  for (const item of equipment) {
    const key = [item.kind, item.manufacturer, item.model].join('|')
    const existing = grouped.get(key)
    if (existing) {
      existing.count += item.count
    } else {
      grouped.set(key, { ...item, name: undefined })
    }
  }

  return (
    <div className="table-responsive">
      <table className="table table-sm">
        <thead>
          <tr>
            <th>Type</th>
            <th>Manufacturer</th>
            <th>Model</th>
            <th>Count</th>
          </tr>
        </thead>
        <tbody>
          {Array.from(grouped.values()).map(item => (
            <tr key={`${item.kind}-${item.manufacturer}-${item.model}`}>
              <td className="text-capitalize">{item.kind.replace(/_/g, ' ')}</td>
              <td>{item.manufacturer || '—'}</td>
              <td>{item.model || '—'}</td>
              <td>{item.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export const OwnerMonitoringSiteDetailPage = () => {
  const { siteId } = useParams<{ siteId: string }>()
  const [refreshVersion, setRefreshVersion] = useState(0)
  const [workingAction, setWorkingAction] = useState<string | null>(null)
  const { fetching, error, data: site } = useApi<MonitoringSiteDetail>(
    siteId ? `/solar-monitoring/owner/sites/${siteId}` : '',
    [refreshVersion],
  )

  const runAction = async (
    action: string,
    path: string,
    body: Record<string, string> = {},
  ): Promise<void> => {
    setWorkingAction(action)
    try {
      await api.post(path, body)
      toast.success(`${action} completed`)
      setRefreshVersion(version => version + 1)
    } catch (actionError) {
      toast.error(errorToString(actionError))
    } finally {
      setWorkingAction(null)
    }
  }

  const refreshPath = site
    ? `/solar-monitoring/owner/sites/${site.id}/refresh`
    : ''
  const backfillPath = site
    ? `/solar-monitoring/owner/sites/${site.id}/backfill`
    : ''

  return (
    <Section>
      <p><Link to="/owner-monitoring">Back to monitoring sites</Link></p>
      <h4>{site?.name || 'Monitoring site details'}</h4>
      {fetching ? <Spinner size={SpinnerSize.large} /> : null}
      {error ? (
        <div className="alert alert-danger">
          {typeof error === 'string' ? error : error.message}
        </div>
      ) : null}
      {site ? (
        <>
          {site.lastSyncErrorCategory ? (
            <div className="alert alert-warning">
              Last provider request returned {site.lastSyncErrorCategory}. Stored data is retained.
            </div>
          ) : null}
          {site.evidence ? (
            <div className="alert alert-info">
              <strong>{getOwnerMonitoringEvidenceLabel(site.evidence.result)}</strong>
            </div>
          ) : null}

          <div className="site-metadata-grid mb-4">
            <MetadataItem label="Provider" value={site.provider} />
            <MetadataItem label="System ID" value={site.externalSystemId} />
            <MetadataItem label="Connection status" value={site.status} />
            <MetadataItem label="Provider status" value={site.providerStatus} />
            <MetadataItem label="Address" value={site.address} />
            {/* <MetadataItem label="Timezone" value={site.timezone} /> */}
            <MetadataItem label="Install date" value={site.installationDate} />
            <MetadataItem
              label="System size"
              value={site.systemSizeWatts !== undefined
                ? `${(site.systemSizeWatts / 1000).toFixed(2)} kW`
                : undefined}
            />
            {/* <MetadataItem label="Modules" value={site.summary?.moduleCount} /> */}
            <MetadataItem
              label="Lifetime production"
              value={site.summary?.energyLifetimeWh !== undefined
                ? formatKwh(site.summary.energyLifetimeWh)
                : undefined}
            />
            <MetadataItem
              label="Last production report"
              value={formatTimestamp(site.summary?.lastProducedAt)}
            />
            <MetadataItem label="Last metadata sync" value={formatTimestamp(site.lastMetadataSyncedAt)} />
            <MetadataItem label="Last energy sync" value={formatTimestamp(site.lastEnergySyncedAt)} />
            {/* <MetadataItem
              label="Provider requests in last action"
              value={site.lastProviderRequestCount}
            /> */}
          </div>

          <h5>Equipment</h5>
          <EquipmentTable equipment={site.equipment || []} />
          <h5 className="mt-4">Daily production</h5>
          <ProductionChart
            readings={site.productionReadings || []}
            currentPowerW={site.summary?.currentPowerW}
          />

          <div className="d-flex flex-wrap gap-2 mb-3">
            <button
              className="btn btn-primary"
              disabled={workingAction !== null}
              onClick={() => runAction('Refresh', refreshPath)}
              type="button"
            >
              {workingAction === 'Refresh' ? 'Refreshing…' : 'Refresh metadata and latest 30 days'}
            </button>
            {site.provider === SolarMonitoringProvider.Enphase ? (
              <button
                className="btn btn-outline-primary"
                disabled={workingAction !== null}
                onClick={() => runAction('Full history backfill', backfillPath, {
                  preset: 'enphase_full',
                })}
                type="button"
              >
                {workingAction === 'Full history backfill'
                  ? 'Backfilling…'
                  : 'Backfill full history'}
              </button>
            ) : (
              <>
                <button
                  className="btn btn-outline-primary"
                  disabled={workingAction !== null}
                  onClick={() => runAction('90-day backfill', backfillPath, {
                    preset: 'solaredge_90_days',
                  })}
                  type="button"
                >
                  {workingAction === '90-day backfill' ? 'Backfilling…' : 'Backfill 90 days'}
                </button>
                <button
                  className="btn btn-outline-primary"
                  disabled={workingAction !== null}
                  onClick={() => runAction('One-year backfill', backfillPath, {
                    preset: 'solaredge_one_year',
                  })}
                  type="button"
                >
                  {workingAction === 'One-year backfill' ? 'Backfilling…' : 'Backfill 1 year'}
                </button>
              </>
            )}
          </div>
        </>
      ) : null}
    </Section>
  )
}
